'use client';

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { ThemeStyle } from './ThemeStyle';
import type { Theme, ThemeToken } from './tokens';

type ThemeContextValue = {
  theme: Theme;
  /** Merges into the current theme rather than replacing it. */
  setTheme: (updates: Theme) => void;
  clearToken: (token: ThemeToken) => void;
  reset: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/* A fresh object literal as a default prop would remount the theme on every render. */
const NO_OVERRIDES: Theme = {};

/**
 * Only needed where a theme changes after load. A site that knows its partner
 * at request time renders <ThemeStyle> from the server instead.
 */
export const ThemeProvider = ({
  initialTheme = NO_OVERRIDES,
  children,
}: {
  initialTheme?: Theme;
  children: ReactNode;
}) => {
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  const setTheme = useCallback((updates: Theme) => {
    setThemeState(current => ({ ...current, ...updates }));
  }, []);

  const clearToken = useCallback((token: ThemeToken) => {
    setThemeState(current => {
      const next = { ...current };
      delete next[token];
      return next;
    });
  }, []);

  const reset = useCallback(() => setThemeState(initialTheme), [initialTheme]);

  const value = useMemo(() => ({ theme, setTheme, clearToken, reset }), [theme, setTheme, clearToken, reset]);

  return (
    <ThemeContext.Provider value={value}>
      <ThemeStyle theme={theme} />
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) throw new Error('useTheme must be used inside a ThemeProvider');
  return context;
};
